import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError } from '@saas-kit/lib';
import {
  Button,
  Card,
  CardContent,
  EmptyState,
  ErrorBanner,
  PageHeader,
  toast,
} from '@saas-kit/ui';
import type { BulkAction, PaginationState } from '@saas-kit/ui/DataTable';

import {
  archiveTenant,
  createTenant,
  listTenants,
  reactivateTenant,
  suspendTenant,
  type AdminTenant,
  type CreateTenantPayload,
} from './api';
import { buildTenantBulkActions } from './bulkActions';
import { TenantCreateForm } from './TenantCreateForm';
import { TenantsTable } from './TenantsTable';

const DEFAULT_PAGE_SIZE = 25;

type ActionKey = 'suspend' | 'reactivate' | 'archive';

interface PendingAction {
  tenantId: string;
  action: ActionKey;
}

export function TenantsPage(): JSX.Element {
  const { t } = useTranslation('adminTenants');
  const queryClient = useQueryClient();

  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchFilter, setSearchFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);

  const trimmedSearch = searchFilter.trim();

  const tenantsQuery = useQuery({
    queryKey: ['admin-tenants', { offset, limit: pageSize, search: trimmedSearch }],
    queryFn: () =>
      listTenants({ offset, limit: pageSize, search: trimmedSearch || null }),
    placeholderData: (prev) => prev,
  });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
  }

  const createMutation = useMutation({
    mutationFn: createTenant,
    onSuccess: invalidate,
  });

  function buildStatusMutation(
    action: ActionKey,
    fn: (id: string) => Promise<AdminTenant>,
    toastKey: string,
  ) {
    return {
      mutationFn: async (tenant: AdminTenant) => {
        setPending({ tenantId: tenant.id, action });
        try {
          return await fn(tenant.id);
        } finally {
          setPending(null);
        }
      },
      onSuccess: async () => {
        await invalidate();
        toast.success(t(toastKey));
      },
      onError: (err: unknown) => toast.error(parseApiError(err).message),
    };
  }

  const suspendMutation = useMutation(
    buildStatusMutation('suspend', suspendTenant, 'toasts.suspended'),
  );
  const reactivateMutation = useMutation(
    buildStatusMutation('reactivate', reactivateTenant, 'toasts.reactivated'),
  );
  const archiveMutation = useMutation(
    buildStatusMutation('archive', archiveTenant, 'toasts.archived'),
  );

  const bulkActions = useMemo<BulkAction<AdminTenant>[]>(
    () => buildTenantBulkActions({ t, invalidate }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  async function handleCreate(payload: CreateTenantPayload): Promise<void> {
    await createMutation.mutateAsync(payload);
  }

  function handleSearchChange(next: string): void {
    setSearchFilter(next);
    setOffset(0);
  }

  const tenants = tenantsQuery.data?.items ?? [];
  const page = tenantsQuery.data?.page;
  const total = page?.total ?? 0;
  const hasNext = page ? page.offset + page.limit < page.total : false;
  const hasPrev = offset > 0;

  const pagination: PaginationState = {
    pageIndex: Math.floor(offset / pageSize),
    pageSize,
    hasNext,
    hasPrev,
    onNext: () => setOffset(offset + pageSize),
    onPrev: () => setOffset(Math.max(0, offset - pageSize)),
    onPageSizeChange: (n) => {
      setPageSize(n);
      setOffset(0);
    },
  };

  const showEmpty =
    !tenantsQuery.isLoading &&
    total === 0 &&
    !tenantsQuery.isError &&
    !trimmedSearch;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('title')}
        description={t('subtitle')}
        actions={
          <Button onClick={() => setCreateOpen(true)}>{t('actions.create')}</Button>
        }
      />

      {tenantsQuery.isError ? (
        <ErrorBanner>{t('errors.load_failed')}</ErrorBanner>
      ) : null}

      {showEmpty ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState title={t('empty.title')} description={t('empty.body')} />
          </CardContent>
        </Card>
      ) : (
        <TenantsTable
          tenants={tenants}
          pendingTenantId={pending?.tenantId ?? null}
          pendingAction={pending?.action ?? null}
          pagination={pagination}
          isLoading={tenantsQuery.isLoading}
          error={tenantsQuery.isError ? t('errors.load_failed') : null}
          bulkActions={bulkActions}
          onSuspend={(tt) => suspendMutation.mutate(tt)}
          onReactivate={(tt) => reactivateMutation.mutate(tt)}
          onArchive={(tt) => archiveMutation.mutate(tt)}
          search={searchFilter}
          onSearchChange={handleSearchChange}
          searchPlaceholder={t('filters.search_placeholder')}
        />
      )}

      <TenantCreateForm
        open={createOpen}
        onOpenChange={setCreateOpen}
        isPending={createMutation.isPending}
        onSubmit={handleCreate}
      />
    </div>
  );
}
